import type { CareNavigationRosterMember } from "@t3tools/contracts";

export type CareNavigationTeamId = string;
export type CareNavigationStaffStatus = "active" | "inactive" | "all";

// Preserve existing shared links; membership always comes from the directory.
const LEGACY_TEAM_IDS: Record<string, string> = {
  "stephanie-vah": "stephanie.vah@craniometrix.com",
  "sharon-burn": "sharon.burns@craniometrix.com",
};

export function careNavigationTeamId(value: string | null): CareNavigationTeamId | undefined {
  if (!value) return undefined;
  return (
    LEGACY_TEAM_IDS[value] ?? (/^[^\s@]+@[^\s@]+$/.test(value) ? value.toLowerCase() : undefined)
  );
}

export function careNavigationTeams(members: readonly CareNavigationRosterMember[]) {
  const managers = new Map<string, string>();
  for (const member of members) {
    if (member.managerEmail)
      managers.set(member.managerEmail, member.managerName ?? member.managerEmail);
  }
  return Array.from(managers, ([id, name]) => ({ id, label: `${name}'s team` })).sort(
    (left, right) => left.label.localeCompare(right.label),
  );
}

export function careNavigationScopeMembers(
  members: readonly CareNavigationRosterMember[],
  teamId: CareNavigationTeamId | undefined,
  status: CareNavigationStaffStatus = "active",
) {
  const managerEmail = teamId ? careNavigationTeamId(teamId) : undefined;
  const byEmail = new Map(members.map((member) => [member.email, member]));
  return members.filter((member) => {
    if (status !== "all" && (member.status === "active") !== (status === "active")) return false;
    if (!teamId) return true;
    const visited = new Set<string>();
    let email: string | null = member.email;
    while (email && !visited.has(email)) {
      if (email === managerEmail) return true;
      visited.add(email);
      email = byEmail.get(email)?.managerEmail ?? null;
    }
    return false;
  });
}

export function careNavigationTeamNavigators(
  teamId: CareNavigationTeamId | undefined,
  members: readonly CareNavigationRosterMember[],
  status: CareNavigationStaffStatus = "active",
) {
  return [
    ...new Set(
      careNavigationScopeMembers(members, teamId, status).flatMap((member) =>
        member.navigatorName ? [member.navigatorName] : [],
      ),
    ),
  ].sort();
}

/** Older retained snapshots have labels only. Never guess between same-name accounts. */
export function careNavigationAccountId(
  row: { navigatorUserId?: string | null; navigatorName: string | null },
  members: readonly CareNavigationRosterMember[],
  unmatchedNames: readonly string[] = [],
): string | undefined {
  if (row.navigatorUserId) return row.navigatorUserId;
  if (!row.navigatorName || unmatchedNames.includes(row.navigatorName)) return undefined;
  const matches = members.filter((member) => member.navigatorName === row.navigatorName);
  return matches.length === 1 && matches[0]?.navigatorNameUnique !== false
    ? (matches[0]?.navigatorUserId ?? undefined)
    : undefined;
}

export function careNavigationSelectedMembers(
  members: readonly CareNavigationRosterMember[],
  teamId: CareNavigationTeamId | undefined,
  status: CareNavigationStaffStatus = "active",
  names: readonly string[] = [],
  unmatchedNames: readonly string[] = [],
) {
  const selectedIds = new Set(
    names.map((navigatorName) =>
      careNavigationAccountId({ navigatorName }, members, unmatchedNames),
    ),
  );
  return careNavigationScopeMembers(members, teamId, status).filter(
    (member) =>
      member.navigatorUserId &&
      (teamId || !names.length || selectedIds.has(member.navigatorUserId)),
  );
}
